import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { directoryKeys } from '@/utils/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { getParentPath, isAncestorOrSelf } from '../utils';
import type { FileManagerNavigationState } from '../logic/core/useFileManagerNavigation';
import type { FileSystemItem } from '../types';

export type DeleteItemVariables = {
  item: FileSystemItem;
};

// `navigation` is taken as an explicit param rather than self-injected via
// useFileManagerNavigation() for the same cross-injection reason useMoveItemMutation does: this
// mutation is constructed inside provideFileManagerDelete(), which runs directly in FileManager.vue's
// own setup() — the same instance that provides navigation, which a component can never inject its
// own provide() call from.
//
// Unlike useMoveItemMutation, this deliberately does NOT also take `settings`: deleteItem addresses
// purely by the item's own id/driveId (already resolved on every FileSystemItem the UI hands it), so
// there's no siteId lookup involved the way move's getItemReference()/upload's site-scoped endpoints
// need. Threading an unused settings param through here would be dead weight, not shape parity.
export const useDeleteItemMutation = (
  navigation: Pick<FileManagerNavigationState, 'currentPath' | 'navigateTo'>,
) => {
  const queryClient = useQueryClient();
  const { currentPath, navigateTo } = navigation;

  return useMutation({
    mutationKey: ['directoryService', 'deleteItem'],
    mutationFn: ({ item }: DeleteItemVariables) =>
      sharePointDriveService.deleteItem(item),
    onSuccess: (_result, { item }) => {
      const parentPath = getParentPath(item.path);
      queryClient.invalidateQueries({
        queryKey: directoryKeys.children(parentPath),
      });

      // Same hasFolderChildren-flip reasoning as move: a deleted directory can flip its (former) parent's
      // childCount to zero, and that flag lives on the parent's own entry in the *grandparent's*
      // children list.
      if (item.type === 'directory') {
        queryClient.invalidateQueries({
          queryKey: directoryKeys.children(getParentPath(parentPath)),
        });
      }

      // Unlike move (which follows the item to wherever it landed), a deleted item has nowhere to
      // relocate to — so if it was the current folder or an ancestor of it, the only sane place to
      // land is its parent. No item.type guard here (unlike the grandparent-invalidation branch
      // above): currentPath is always a directory path, so it can only ever equal or be nested under
      // another directory's path in the first place — a file item never satisfies isAncestorOrSelf.
      if (isAncestorOrSelf(item.path, currentPath.value)) {
        navigateTo(parentPath, { replace: true });
      }
    },
    // Deliberately no onError toast here — the same convention useMoveItemMutation and
    // useCopyItemMutation now follow. Failure surfacing is aggregated one level up (in
    // useFileManagerDelete here, runTransfer for the two transfer verbs), so a 5-item delete with 2
    // failures produces one summary toast instead of five. The rejection still propagates untouched,
    // so the orchestrating composable's own mutateAsync().then(..., onRejected) sees it.
  });
};
